import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { LogOut, ShieldCheck } from 'lucide-react';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import { useAuth } from '@/hooks/useAuth';
import { accessRoleLabel } from '@/lib/labels';
import { actingOrgUnitTypeOf } from '@/lib/accessRole';

/**
 * The account menu.
 *
 * ⚠️ IT DELIBERATELY DOES NOT SHOW THE OFFICER'S NAME.
 * The name and the rank/unit line live in the sidebar footer (`OfficerIdentity`), always
 * rendered. Repeating them here would put a second copy in the DOM the moment the menu
 * opened, and "identity appears exactly once" is what makes `AppShell.test.tsx`'s
 * `getByText` assertions stable rather than lucky. The trigger names the officer through
 * `aria-label`, which is an attribute and invisible to `getByText`.
 *
 * ⚠️ ANTD `Dropdown`, NOT A RADIX ONE — a documented exception to CLAUDE.md §8's
 * "shadcn/ui for the shell". `@radix-ui/react-dropdown-menu` pulls in `@floating-ui/dom`,
 * which constructs `ResizeObserver` directly and would need a jsdom stub in
 * `vitest.setup.ts`. AntD ships `resize-observer-polyfill` through `rc-resize-observer` —
 * which is why the existing Officers test can already open a `TreeSelect`. Zero new
 * dependencies, zero new test setup, and it is already themed from `theme/tokens.ts`
 * through `ConfigProvider`, so it cannot become a fourth colour source.
 */
export const AvatarMenu = () => {
  const { officer, signOut } = useAuth();
  if (!officer) return null;

  /* ⚠️ [C4a §8] Named for the node — the same words the officers list and Duty Mitra's profile use. */
  const roleLabel = accessRoleLabel(officer.accessRole, actingOrgUnitTypeOf(officer));

  const items: MenuProps['items'] = [
    {
      key: 'identity',
      type: 'group',
      label: (
        <span className="block py-1">
          <span className="block text-xs text-muted-foreground">{officer.phone}</span>
          <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <ShieldCheck className="size-3" aria-hidden />
            {roleLabel}
          </span>
        </span>
      ),
    },
    { type: 'divider' },
    {
      key: 'sign-out',
      label: 'Sign out',
      icon: <LogOut className="size-4" aria-hidden />,
      onClick: () => void signOut(),
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" overlayClassName="min-w-56">
      <button
        type="button"
        aria-label={`Account menu for ${officer.name}`}
        data-testid="avatar-menu"
        className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <OfficerAvatar name={officer.name} />
      </button>
    </Dropdown>
  );
};
